import Link from '@/lib/nav/link';
import { usePathname } from '@/lib/nav/navigation';
import { readTripResume, tripResumeHref } from '@/lib/nav/trip-resume';
import { PrivateIcon as ShieldCheck } from '@/components/icons/line/private';
import { RoamIcon, playRoamIconMotion } from '@/components/icons/RoamIcon';
import { useUser, UserAvatar } from '@/lib/auth/client-auth';
import { BETA_FEATURES } from '@/lib/beta-features';
import { TripSwitcher, type TripOption } from './TripSwitcher';

interface Props {
  email: string;
  trips?: TripOption[];
  /** Last accessible trip, used only for the return shortcut on global pages. */
  lastActiveTripId?: string | null;
  showTripSwitcher?: boolean;
  showAdminNav?: boolean;
  /**
   * Called when a nav link is clicked. The mobile drawer uses this to
   * close itself; the desktop sidebar leaves it undefined.
   */
  onNavigate?: () => void;
}

/**
 * Shared nav surface used by both the desktop Sidebar (always-mounted
 * fixed left rail) and the MobileNavDrawer (Sheet that slides in from
 * the left below md). Same items and active-detection logic keep the
 * two surfaces from drifting.
 */
export function SidebarNav({
  email,
  trips = [],
  lastActiveTripId = null,
  showTripSwitcher = true,
  showAdminNav = false,
  onNavigate,
}: Props) {
  const pathname = usePathname();
  const { user } = useUser();
  const tripIdMatch = /^\/trips\/([^/]+)/i.exec(pathname);
  const currentTripId = trips.some((trip) => trip.id === tripIdMatch?.[1])
    ? tripIdMatch![1]!
    : null;
  const isNewTrip = pathname === '/new-trip' || pathname.startsWith('/new-trip/');
  const remembered = readTripResume(
    user?.id,
    trips.map((trip) => trip.id),
  );
  const resumeTrip = trips.find((trip) => trip.id === (remembered?.tripId ?? lastActiveTripId));
  const resumeHref = resumeTrip
    ? (remembered?.href ??
      tripResumeHref(resumeTrip.id, `/trips/${resumeTrip.id}`, 'view=itinerary'))
    : null;

  const exploreBetaFeature = BETA_FEATURES.find((feature) => feature.id === 'public-explore');

  const items: Array<{
    label: string;
    href: string;
    iconName?:
      | 'explore'
      | 'ask-roam'
      | 'calendar'
      | 'details'
      | 'itinerary'
      | 'sleep'
      | 'trips'
      | 'new-trip'
      | 'unknown';
    customIcon?: React.ReactNode;
    badge?: string;
    isActive: boolean;
  }> = [
    {
      label: 'All trips',
      href: '/trips',
      iconName: 'trips',
      isActive: pathname === '/trips' || pathname.startsWith('/trips/active'),
    },
    {
      label: 'Year view',
      href: '/year',
      iconName: 'calendar',
      isActive: pathname === '/year' || pathname.startsWith('/year/'),
    },
    {
      label: 'Explore',
      href: '/explore',
      iconName: 'explore',
      badge: exploreBetaFeature ? 'Beta' : undefined,
      isActive: pathname.startsWith('/explore'),
    },
    {
      label: 'New trip',
      href: '/new-trip',
      iconName: 'new-trip',
      isActive: isNewTrip,
    },
    {
      label: 'Help',
      href: '/help',
      iconName: 'unknown',
      isActive: pathname.startsWith('/help'),
    },
    ...(showAdminNav
      ? [
          {
            label: 'Admin',
            href: '/admin',
            customIcon: <ShieldCheck className="h-4 w-4" />,
            isActive: pathname.startsWith('/admin'),
          },
        ]
      : []),
  ];

  const isOnSettings = pathname.startsWith('/settings');
  const initials =
    [user?.firstName, user?.lastName]
      .map((part) => part?.trim().charAt(0) ?? '')
      .join('')
      .slice(0, 2)
      .toUpperCase() ||
    email.trim().charAt(0).toUpperCase() ||
    'R';

  return (
    <>
      {showTripSwitcher && (currentTripId || (resumeTrip && resumeHref)) ? (
        <div className="px-5 pt-2">
          {currentTripId ? (
            <TripSwitcher
              trips={trips}
              currentTripId={currentTripId}
              variant="sidebar"
              onNavigate={onNavigate}
            />
          ) : resumeTrip && resumeHref ? (
            <Link
              href={resumeHref}
              onClick={onNavigate}
              aria-label={`Return to ${resumeTrip.name}`}
              title={resumeTrip.name}
              className="flex min-h-11 min-w-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-ink/80 transition hover:bg-muted hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <RoamIcon name="back" className="h-4 w-4 shrink-0" aria-hidden />
              <span className="min-w-0 flex-1 truncate">{resumeTrip.name}</span>
            </Link>
          ) : null}
        </div>
      ) : null}

      <nav aria-label="App menu" className="space-y-1 px-5 pt-5">
        {items.map((it) => (
          <Link
            key={it.label}
            href={it.href}
            aria-current={it.isActive ? 'page' : undefined}
            onClick={(event) => {
              playRoamIconMotion(event);
              onNavigate?.();
            }}
            className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition ${
              it.isActive ? 'bg-primary/10 text-ink' : 'text-ink/80 hover:bg-muted hover:text-ink'
            }`}
          >
            {it.customIcon ?? (
              <RoamIcon
                name={it.iconName!}
                className="h-4 w-4"
                variant={it.isActive ? 'solid' : 'line'}
                selected={it.isActive}
                aria-hidden
              />
            )}
            <span className="min-w-0 flex-1">{it.label}</span>
            {it.badge ? (
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wide ${
                  it.isActive ? 'bg-primary/10 text-[#a33a27]' : 'bg-[#ffb178]/20 text-[#a33a27]'
                }`}
              >
                {it.badge}
              </span>
            ) : null}
          </Link>
        ))}
      </nav>

      {/* One account destination, regardless of whether the user clicks the
          photo, email, label, or surrounding tile. Account actions live on
          Settings instead of behind Clerk's avatar popover. */}
      <div className="mt-auto px-5 pb-5 pt-6">
        <Link
          href="/settings"
          onClick={(event) => {
            playRoamIconMotion(event);
            onNavigate?.();
          }}
          className={`group flex items-center gap-2.5 rounded-xl border px-2.5 py-2 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/30 ${
            isOnSettings
              ? 'border-primary/30 bg-primary/10'
              : 'border-ink/8 bg-muted/40 hover:border-ink/15 hover:bg-muted'
          }`}
          title={email}
          aria-label={`Open account settings for ${email}`}
        >
          <AccountAvatar initials={initials} />
          <span className="min-w-0 flex-1 px-1 py-0.5">
            <span className="block truncate text-xs font-bold text-ink/85">{email}</span>
            <span className="block text-[11px] font-semibold text-ink/45 transition group-hover:text-ink/75">
              Account &amp; family
            </span>
          </span>
          <RoamIcon
            name="forward"
            className="h-4 w-4 shrink-0 text-ink/30 transition group-hover:translate-x-0.5 group-hover:text-ink/55"
            aria-hidden
          />
        </Link>
      </div>
    </>
  );
}

function AccountAvatar({ initials }: { initials: string }) {
  const fallback = (
    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-coral/10 text-[11px] font-black text-coral">
      {initials}
    </span>
  );
  return (
    <span
      className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-coral/10 ring-1 ring-ink/10"
      data-testid="sidebar-account-avatar"
      aria-hidden
    >
      <UserAvatar fallback={fallback} />
    </span>
  );
}
